// @flow
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose, withProps } from 'recompose';
import Helmet from 'react-helmet';
import pluralize from 'pluralize';
import slugify from 'slugify';
import {
  difference,
  get,
  flow,
  filter,
  head,
  intersection,
  isEmpty,
  map,
  keyBy,
  noop,
  omit,
  set,
  size,
  sortBy,
  toString,
  uniq,
  values,
  without
} from 'lodash/fp';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import styled from '@emotion/styled';

import FriendsLoader from 'app/components/friends/common/FriendsLoader';
import entitiesSelector from 'app/selectors/entitiesSelector';
import Button from 'app/components/commons/Button';
import {
  convertPositionForFantasyInsider,
  isChallengeAFL,
  isChallengeGolf,
  COMPETITION_ORIGIN
} from 'app/utils/challengeUtils';
import { getUpcomingChallenges, saveCrunchedLineUps } from 'app/api/challenges';
import { GOLF } from 'app/constants/challengeOneSportIds';
import {
  fetchChallenge,
  resetChallenge,
  resetLineUp,
  updatePlayers
} from 'app/actions/challengeActions';
import { toggleSelectedTeamsFilter } from 'app/actions/playerListActions';
import {
  setPlayers,
  updateSettings,
  resetFantasyInsier,
  resetExposureSettings
} from 'app/actions/fantasyInsiderActions';
import Loader from 'app/components/commons/Loader';
import FlexiSelect from 'app/components/commons/FlexiSelect';
import { getSportCategories } from 'app/actions/sportCategoriesActions';
import withAPICollection from 'app/hoc/withAPICollection';
import Page from 'app/containers/parts/Page';
import { getSlates } from 'app/api/h2h';
import lineUpSelector from 'app/selectors/lineUpSelector';
import UserOnly from 'app/components/util/UserOnly';
import Modal from 'app/components/commons/Modal';
import FormValidationMessage from 'app/components/commons/FormValidationMessage';
import { hover } from 'app/utils/cssUtils';

import { getInsiderSport, crunchTeams } from './api';
import LineUpBuilder from './components/LineUpBuilder';
import normalisePlayer from './normalisePlayer';
import SportTabs from './components/SportsTab';
import LineUpGrid from './components/LineUpGrid';
import Tabs from './components/Tabs';
import decodeCrunchedLineUps from './decodeCrunchedLineUps';
import TermsModal from './components/TermsModal';
import Logo from './components/Logo';
import { normaliseSportShortName, shouldUseAverage } from './slateUtils';

type Props = {
  bonuses?: Object,
  competitionName?: string,
  labels?: Array<Object>,
  scheme?: 'light' | 'dark' | 'white',
  history: Object,
  selectedTeams?: Array<string>,
  hasInitialised?: boolean,
  sportsInitialised?: boolean,
  getCategories: () => void,
  onUpdateSettings: (settings: Object) => void,
  isLoading?: boolean,
  insiderInitialised?: boolean,
  isNBA?: boolean,
  isSingleGameChallenge?: boolean,
  challengeTeams?: Object,
  defaultMaxPlayersPerTeam?: number,
  defaultSalaryCap?: number,
  insiderSettings?: Object,
  siteName?: string,
  slatesInitialised?: boolean,
  isAFL?: boolean,
  insiderPlayers?: Object,
  lineUp?: Array<Object>,
  loadChallenge: () => void,
  onResetFantasyInsider: () => void,
  onFetch: () => void,
  onResetChallenge: () => void,
  onResetLineUp: () => void,
  entities?: Array<Object>,
  requiredSelections?: number,
  slates?: Array<Object>,
  sportsList?: Array<Object>,
  onTeamsChange: (string | array) => void,
  onResetExposureSettings: (settings?: Object) => void,
  onSetInsiderPlayers: (players: Object | null) => void,
  onUpdatePlayers: () => void,
  isNativeWebView: boolean
};

const LoaderWrapper = styled.div({
  position: 'relative',
  height: '400px'
});

const defaultProps = {
  siteName: undefined,
  competitionName: undefined,
  insiderInitialised: false,
  isSingleGameChallenge: false,
  bonuses: undefined,
  slatesInitialised: false,
  labels: [],
  lineUp: [],
  sportsInitialised: false,
  challengeTeams: undefined,
  scheme: 'light',
  selectedTeams: null,
  isLoading: false,
  isAFL: false,
  isNBA: false,
  defaultMaxPlayersPerTeam: undefined,
  defaultSalaryCap: undefined,
  insiderSettings: undefined,
  insiderPlayers: undefined,
  hasInitialised: false,
  entities: [],
  slates: undefined,
  sportsList: [],
  requiredSelections: undefined
};

const tabs = [
  {
    id: 'teams',
    label: 'Customise Teams'
  },
  {
    id: 'settings',
    label: 'Settings'
  },
  {
    id: 'floorCeiling',
    label: 'Floor / Ceiling'
  },
  {
    id: 'importExport',
    label: 'Import / Export'
  },
  {
    id: 'saveLoadProjection',
    label: 'Save / Load'
  },
  {
    id: 'labels',
    label: 'Labels'
  }
];

const tabsWithoutTeam = [
  {
    id: 'settings',
    label: 'Settings'
  },
  {
    id: 'importExport',
    label: 'Import / Export'
  },
  {
    id: 'saveLoadProjection',
    label: 'Save / Load'
  },
  {
    id: 'labels',
    label: 'Labels'
  }
];

const ACCEPTED_TERMS_KEY_OLD = 'accepted_fantasy_cruncher_v3';
const ACCEPTED_TERMS_KEY = 'accepted_fantasy_cruncher_v4';

const StyledFlexiSelect = styled(FlexiSelect)({
  flexGrow: 1
});

const Row = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
});

const crunchOptions = ({
  salaryCap,
  minSalary,
  numLineUps,
  uniquePlayers,
  teamStacking,
  numPlayersForTeamStacking,
  portfolioMethod,
  minCeiling,
  maxPlayersPerTeam
}) => ({
  salaryCap,
  minSalary,
  Lineups: numLineUps,
  UniquePlayers: uniquePlayers,
  Platform: 'DraftStars',
  StackTeam:
    Array.isArray(teamStacking) && teamStacking.length > 0 ? map('value', teamStacking) : '',
  StackNumber: numPlayersForTeamStacking,
  portmethod: portfolioMethod,
  ExposurePct: 1,
  MinCeiling: minCeiling,
  MaxPlayers: maxPlayersPerTeam,
  RandomSD: 0
});

const DEFAULT_PROJECTION = 15;
const enableTermsModal = true;

export const getPlayerFields = p => ({
  Lock: p.selected ? 1 : 0,
  MaxExposure: p.insiderExposure,
  Name: p.insiderName,
  playerID: p.insiderId,
  Position: p.selectedPosition || p.insiderPosition,
  Position2: p.selectedPosition2 || p.insiderPosition2,
  activePos: p.activePos || undefined,
  Price: p.salary,
  Rating: p.insiderScoreUpdated,
  Status: p.insiderStatus,
  StdDev: p.insiderStdDev,
  Team: p.insiderTeam,
  warning: p.insiderWarning ? 1 : 0
});

const FantasyInsider = ({
  bonuses,
  competitionName,
  siteName,
  labels,
  scheme,
  getCategories,
  history,
  hasInitialised,
  isAFL,
  isNBA,
  isLoading,
  isSingleGameChallenge,
  loadChallenge,
  insiderSettings,
  insiderInitialised,
  insiderPlayers,
  onFetch,
  onResetChallenge,
  onResetLineUp,
  onResetFantasyInsider,
  defaultMaxPlayersPerTeam,
  sportsInitialised,
  defaultSalaryCap,
  onUpdatePlayers,
  requiredSelections,
  onUpdateSettings,
  onResetExposureSettings,
  selectedTeams,
  slatesInitialised,
  entities,
  challengeTeams,
  slates,
  lineUp,
  onTeamsChange,
  sportsList,
  onSetInsiderPlayers,
  isNativeWebView
}: Props) => {
  const [selectedSport, setSelectedSport] = useState(undefined);
  const [selectedSlate, setSelectedSlate] = useState(undefined);
  const [challenges, setChallenges] = useState([]);
  const [error, setError] = useState(undefined);
  const [teams, setTeams] = useState(undefined);
  const [isCrunching, setIsCrunching] = useState(false);
  const [savedLineUps, setSavedLineUps] = useState([]);
  const [lineUpErrors, setLineUpErrors] = useState(undefined);
  const [activeTab, setActiveTab] = useState(undefined);
  const [activeSettingsTab, setActiveSettingsTab] = useState('basic');
  const [keepCurrentLineUps, setKeepCurrentLineUps] = useState(true);
  const [removedPlayers, setRemovedPlayers] = useState({});
  const [challengesLoaded, setChallengesLoaded] = useState(false);
  const [loadingChallenge, setLoadingChallenge] = useState(false);
  const [message, setMessage] = useState(undefined);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [allLineUpsSaved, setAllLineUpsSaved] = useState(false);

  const handleReset = () => {
    setSelectedSlate(undefined);
    setTeams(undefined);
    setError(undefined);
    setSavedLineUps([]);
    setLineUpErrors(undefined);
    setActiveTab(undefined);
    onResetChallenge();
    onResetLineUp();
  };

  const getPortfolioMethod = (options = {}) => {
    if (options.isCaptainContest) return 'degrade1';

    if (options.isSingleGameChallenge) {
      return options.competitionName === 'NFL' ? 'unique' : 'degrade1';
    }

    return 'unique';
  };

  const isCaptainContest = get('isCaptainContest', selectedSlate);

  const handleSelectSlate = async (id, sport) => {
    handleReset();
    onResetExposureSettings({
      portfolioMethod: getPortfolioMethod({ isSingleGameChallenge, competitionName })
    });

    // console.log('handleSelectSlate', id, sport);

    const slate = slates.find(s => s.id === id);

    if (slate) {
      const sportSlug = get('slug', sport || selectedSport);
      let insiderPlayersData;

      if (sportSlug === 'cricket') {
        insiderPlayersData = await getInsiderSport(
          slate.oneSportCompetitionId === COMPETITION_TYPE_BBL ? 'cricket-bb' : 'cricket-t20'
        );
      } else {
        insiderPlayersData = await getInsiderSport(
          slate.oneSportCompetitionId === COMPETITION_ORIGIN ? 'origin' : sportSlug
        );
      }

      if (!Array.isArray(insiderPlayersData) || insiderPlayersData.length === 0) {
        setError('NO PLAYERS AVAILABLE');
        return;
      }

      const fiPlayers = (insiderPlayersData || []).map(p =>
        normalisePlayer(p, { isGolf: slate.isGolf })
      );
      onSetInsiderPlayers(keyBy('insiderIdStr', fiPlayers));

      const challenge = challenges.find(c => c.slateId === slate.id);
      setSelectedSlate(slate);

      if (challenge) {
        setLoadingChallenge(true);
        loadChallenge(challenge.id);
      } else {
        setError('NO CONTEST');
      }
    }
  };

  const handleCloseMessage = () => setMessage(undefined);

  const handleSelectSport = sport => {
    console.log('handleSelectSport', sport);

    if (sport && get('id', selectedSport) === get('id', sport)) {
      return;
    }

    handleReset();
    onResetFantasyInsider();
    setSelectedSport(sport);

    try {
      let defaultSlate = {};

      if (sport.filterKey === 'sportId') {
        defaultSlate =
          slates.find(({ sportId }) => get('filterValues', sport).includes(sportId)) || {};
      }

      if (sport.filterKey === 'competitionId') {
        defaultSlate =
          slates.find(({ oneSportCompetitionId }) =>
            get('filterValues', sport).includes(oneSportCompetitionId)
          ) || {};
      }

      handleSelectSlate(defaultSlate.id, sport);
    } catch (err) {
      onSetInsiderPlayers(null);
      setError('ERROR: NO PLAYERS AVAILABLE');
    }
  };

  const preparing = isLoading || !challengesLoaded || loadingChallenge;

  const handleRemovePlayer = (lineUpId: string, entityIdStr: string) => {
    const lineUpSelection = (get(`${lineUpId}.selection`, teams) || []).map(p => ({
      ...p,
      isRemoved: p.idStr === entityIdStr ? true : p.isRemoved
    }));
    const lineUpRemovedPlayers = get(lineUpId, removedPlayers) || [];
    setRemovedPlayers(set([lineUpId], [...lineUpRemovedPlayers, entityIdStr], removedPlayers));
    setTeams(set([lineUpId, 'selection'], lineUpSelection, teams));
  };

  const handleDeleteLineUp = (lineUpId: string) => setTeams(omit(lineUpId, teams));

  const handleClearLineUps = () => setTeams(undefined);

  const {
    maxExposurePerPlayer,
    avoidWarning,
    avoidLow100s,
    avoidQuestionable,
    ignoreProjections
  } = insiderSettings;

  const crunchFilter = filter(
    ({
      insiderId,
      isExcluded,
      isTeam,
      included,
      isQuestionable,
      insiderWarning,
      insiderLow100s,
      teamId
    }) =>
      !!insiderId &&
      !isExcluded &&
      (included || isNBA || isTeam) &&
      (!avoidWarning || !insiderWarning || isTeam) &&
      (!avoidLow100s || !isAFL || !insiderLow100s || isTeam) &&
      (!avoidQuestionable || !isQuestionable || isTeam) &&
      (!Array.isArray(selectedTeams) || selectedTeams.includes(teamId))
  );

  const handleCrunchTeams = (entitiesData: Array<Object>) => {
    setIsCrunching(true);
    setError(undefined);
    setLineUpErrors(undefined);

    if (!keepCurrentLineUps) {
      handleClearLineUps();
    }

    window.scrollTo(0, document.body.scrollHeight);

    crunchTeams({
      ...crunchOptions(insiderSettings),
      LineupList: crunchFilter(entitiesData).map(e => {
        const lockedPlayer = lineUp.find(l => l.idStr === e.idStr);

        let selectedPosition = e.insiderPosition;

        if (get('positions', lockedPlayer)) {
          const position =
            (lockedPlayer.positions || []).find(pos => pos.key === lockedPlayer.positionId) || {};
          selectedPosition = position.short_label || position.abbr;
        }

        selectedPosition = convertPositionForFantasyInsider(selectedPosition);
        const selectedPosition2 =
          selectedPosition === e.insiderPosition ? e.insiderPosition2 : e.insiderPosition;

        return getPlayerFields({
          ...e,
          selected: lockedPlayer ? 1 : 0,
          selectedPosition,
          selectedPosition2,
          insiderExposure: e.insiderExposure || maxExposurePerPlayer,
          insiderScoreUpdated: ignoreProjections ? DEFAULT_PROJECTION : e.insiderScoreUpdated
        });
      })
    })
      .then(({ data }) => {
        if (data && data.Lineups && data.Lineups.length > 0) {
          const decodedLineUps = decodeCrunchedLineUps(
            data.Lineups,
            selectedSlate.positions,
            entities
          );
          const newTeams = keyBy('lineUpId', decodedLineUps);
          let duplicatesNum = decodedLineUps.length - size(newTeams);

          // const groupedLineUps = groupBy('lineUpId', decodedLineUps);
          // console.log('all lineups', decodedLineUps);
          // console.log('lineups grouped by id', groupedLineUps);

          if (keepCurrentLineUps && teams) {
            const lineUpIds = Object.keys(teams || {});
            const newLineUpIds = Object.keys(newTeams);
            const duplicateLineUpIds = intersection(lineUpIds, newLineUpIds);

            duplicatesNum += duplicateLineUpIds.length;
          }

          if (duplicatesNum > 0) {
            setMessage(
              `${duplicatesNum} duplicate ${pluralize(
                'lineup',
                duplicatesNum
              )} were removed from this crunch, the remaining lineups have been added to 'YOUR CRUNCHED LINEUPS' section.`
            );
          }
          setTeams(keepCurrentLineUps && teams ? { ...newTeams, ...teams } : newTeams);
          setAllLineUpsSaved(false);
          return;
        }

        setError('NO LINEUPS POSSIBLE');
        if (!keepCurrentLineUps) {
          setTeams(undefined);
        }
      })
      .catch(() => {
        setError('NO LINEUPS POSSIBLE');
        if (!keepCurrentLineUps) {
          setTeams(undefined);
        }
      })
      .finally(() => {
        setIsCrunching(false);
      });
  };

  const handleRecalculate = (lineUpId: string) => {
    const lineUpRemovedPlayers = get(lineUpId, removedPlayers) || [];

    setLineUpErrors(omit(lineUpId, lineUpErrors));

    const lineUpSelection = get(`${lineUpId}.selection`, teams);

    crunchTeams({
      ...crunchOptions({ ...insiderSettings, defaultSalaryCap, defaultMaxPlayersPerTeam }),
      Lineups: 1,
      LineupList: crunchFilter(entities)
        .filter(({ insiderIdStr }) => lineUpRemovedPlayers.includes(insiderIdStr) === false)
        .map(p => {
          return {
            ...getPlayerFields({
              ...p,
              activePos: get(
                'activePos',
                lineUpSelection.find(s => s.idStr === p.idStr)
              ),
              insiderExposure: p.insiderExposure || maxExposurePerPlayer,
              insiderScoreUpdated: ignoreProjections ? DEFAULT_PROJECTION : p.insiderScoreUpdated
            }),
            Lock: get(`${lineUpId}.selection`, teams).find(({ idStr }) => idStr === p.idStr) ? 1 : 0
          };
        })
    })
      .then(({ data }) => {
        if (data && data.Lineups && data.Lineups.length > 0) {
          setTeams(
            set(
              [lineUpId],
              head(
                decodeCrunchedLineUps(data.Lineups, selectedSlate.positions, entities, lineUpId)
              ),
              teams
            )
          );
          setSavedLineUps(without([lineUpId], savedLineUps));
          setAllLineUpsSaved(false);
        }
      })
      .catch(() => {
        setLineUpErrors({ ...lineUpErrors, [lineUpId]: 'No LineUp Possible.' });
      });
  };

  const handleConfirmTerms = () => {
    try {
      window.localStorage.setItem(ACCEPTED_TERMS_KEY, 'yes');
      setShowTermsModal(false);
    } catch (err) {
      //
    }
  };

  const handleRejectTerms = () => {
    setShowTermsModal(false);
    history.push('/');
  };

  const handleMarkEntered = (lineUpId: string, entered: boolean) =>
    setTeams(set([lineUpId, 'entered'], entered, teams));

  const handleResetEntered = () =>
    setTeams(
      keyBy(
        'lineUpId',
        Object.values(teams).map(t => ({ ...t, entered: false }))
      )
    );

  const handleOpenSettingsTab = (settingsTab: string) => {
    if (activeTab !== 'settings') {
      setActiveTab('settings');
    }
    setActiveSettingsTab(settingsTab);
  };

  const handleSaveLineUps = (lineUps: Array<Object>, saveAll: boolean = false) => {
    const lineUpIds = map('lineUpId', lineUps);
    saveCrunchedLineUps({
      lineups: lineUps.map(({ selection }) => ({
        selection: selection.map(({ id, isTeam }) => (isTeam ? `T${id}` : id)),
        featured: false
      })),
      slate_id: selectedSlate.id
    })
      .then(({ data: { successes, failures } } = {}) => {
        const errors = {};
        if (Array.isArray(failures) && failures.length) {
          for (let i = 0; i < failures.length; i += 1) {
            const [index, msg] = failures[i].split(': ');
            const indexNum = parseInt(index.substring(6), 10) - 1;
            const lineUpId = get(indexNum, lineUpIds);
            errors[lineUpId] = msg;
          }
          setLineUpErrors({ ...lineUpErrors, ...errors });
        }
        if (successes) {
          const succeededLineUpIds = difference(lineUpIds, Object.keys(errors) || []);
          setSavedLineUps([...savedLineUps, ...succeededLineUpIds]);

          if (saveAll) {
            setAllLineUpsSaved(true);
          }
        }
      })
      .catch(() => {
        //
      });
  };

  const slateOptions = React.useMemo(() => {
    let filteredSlates = slates;

    if (selectedSport) {
      if (selectedSport.filterKey === 'competitionId')
        filteredSlates = slates.filter(({ oneSportCompetitionId }) =>
          selectedSport.filterValues.includes(oneSportCompetitionId)
        );
      else
        filteredSlates = slates.filter(({ sportId }) =>
          selectedSport.filterValues.includes(sportId)
        );
    }
    // console.log('*** slateOptions ***');
    // console.log('selectedSport', selectedSport);
    // console.log('slates', slates);
    // console.log('filteredSlates', filteredSlates);

    const options = filteredSlates.map(({ id: value, name, startTime }) => ({
      value,
      label: `${name} = ${moment(startTime).format('dddd h:mm a')}`
    }));

    return options;
  }, [selectedSport, slates]);

  // introducing a new matching method between slates and sports, the old method was based on enabledSports and
  // enabledSportShortNames, but a lot of special cases and exceptions were needed to cover all the scenarios
  // matching based on enabledSportIds and enabledCompetitionIds should cover all the scenarios
  const [
    // enabledSports, // probably not needed anymore
    // enabledSportShortNames, // probably not needed anymore
    enabledSportIds,
    enabledCompetitionIds
  ] = React.useMemo(() => {
    // const enabledSports = uniq((slates || []).map(({ sportSlug }) => sportSlug));
    // const enabledSportShortNames = uniq((slates || []).map(({ sportShortName }) => sportShortName));

    const enabledSportIds = uniq((slates || []).map(({ sportId }) => sportId));
    const enabledCompetitionIds = uniq(
      (slates || []).map(({ oneSportCompetitionId }) => oneSportCompetitionId)
    );

    return [
      // enabledSports,
      // enabledSportShortNames,
      enabledSportIds,
      enabledCompetitionIds
    ];
  }, [slates]);

  const enabledSportList = React.useMemo(() => {
    return sportsList.filter(sport => {
      if (sport.filterKey === 'sportId') {
        return sport.filterValues.some(filterValue => enabledSportIds.includes(filterValue));
      } else if (sport.filterKey === 'competitionId') {
        return sport.filterValues.some(filterValue => enabledCompetitionIds.includes(filterValue));
      }
    });
  }, [sportsList, enabledSportIds, enabledCompetitionIds]);

  // init Effect
  useEffect(() => {
    onFetch();

    if (!Array.isArray(sportsList) || sportsList.length === 0) {
      getCategories();
    }

    getUpcomingChallenges().then(({ data }) => {
      setChallenges(data.map(({ id, slate_id: slateId }) => ({ id, slateId })));
      setChallengesLoaded(true);
    });

    try {
      window.localStorage.removeItem(ACCEPTED_TERMS_KEY_OLD);
      if (!window.localStorage.getItem(ACCEPTED_TERMS_KEY)) {
        setShowTermsModal(true);
      }
    } catch (err) {
      //
    }

    return () => {
      handleReset();
      onResetFantasyInsider();
    };
  }, []);

  // slates initialised effect
  useEffect(() => {
    if (
      !(
        challengesLoaded &&
        slatesInitialised &&
        sportsInitialised &&
        enabledSportList.length > 0 &&
        !selectedSport
      )
    )
      return;

    let defaultSport;

    // old matching method
    // enabledSports.forEach(es => {
    //   if (!defaultSport) {
    //     defaultSport = sportsList.find(s => s.slug === es);
    //   }
    // });

    // old matching method
    // if (!defaultSport) {
    //   enabledSportShortNames.forEach(sn => {
    //     if (!defaultSport) {
    //       defaultSport = sportsList.find(s => s.slug === sn);
    //     }
    //   });
    // }

    // console.log('***** matching starts here *****');
    // console.log('enabledSportIds', enabledSportIds);
    // console.log('enabledCompetitionIds', enabledCompetitionIds);

    // Alternative matching method that should cover also the previous 2
    // (needs to be tested in more scenarios)
    if (!defaultSport) {
      defaultSport = enabledSportList.find(sport => {
        return sport.filterValues.find(filterValue => {
          if (sport.filterKey === 'sportId' && enabledSportIds.includes(filterValue)) {
            return sport;
          } else if (
            sport.filterKey === 'competitionId' &&
            enabledCompetitionIds.includes(filterValue)
          ) {
            return sport;
          }
        });
      });
    }

    // console.log('sportslist to match', enabledSportList);
    // console.log('matched defaultSport', defaultSport);

    handleSelectSport(defaultSport);
  }, [
    enabledSportList,
    slatesInitialised,
    sportsInitialised,
    challengesLoaded,
    enabledSportIds,
    enabledCompetitionIds
  ]);

  // challenge loaded effect
  useEffect(() => {
    if (!hasInitialised || !insiderInitialised) return;

    onResetLineUp();
    if (!isEmpty(challengeTeams)) {
      onTeamsChange(Object.keys(challengeTeams));
    }
    onUpdateSettings({
      salaryCap: defaultSalaryCap,
      maxPlayersPerTeam: defaultMaxPlayersPerTeam,
      portfolioMethod: getPortfolioMethod({
        isSingleGameChallenge,
        competitionName,
        isCaptainContest
      })
    });

    if (!isEmpty(insiderPlayers)) {
      const newEntities = Object.values(insiderPlayers).map(p => {
        const player = entities.find(e => e.idStr === p.insiderIdStr) || {};

        return {
          ...p,
          insiderScore:
            selectedSlate && !selectedSlate.isGolf && player && player.isReserved
              ? 0
              : p.insiderScore,
          insiderScoreUpdated:
            selectedSlate && !selectedSlate.isGolf && player && player.isReserved
              ? 0
              : p.insiderScoreUpdated
        };
      });
      onUpdatePlayers(keyBy('insiderIdStr', newEntities));
    }
    setLoadingChallenge(false);
  }, [hasInitialised, insiderInitialised]);

  if (slatesInitialised && slates.length === 0) {
    return (
      <Page
        title="Fantasy Insider"
        onBack={() => history.push('/lobby')}
        disableReferrerBack
        fullWidth
      >
        {!isNativeWebView && (
          <BackButton onClick={history.goBack}>
            <BackIcon>
              <span className="ion-ios-arrow-back" />
            </BackIcon>
            Back
          </BackButton>
        )}
        <h3 style={{ margin: '10px auto' }}>No contest available, please check back later.</h3>
      </Page>
    );
  }

  if (sportsList.length === 0 || !selectedSport) {
    return <Loader pad />;
  }

  return (
    <Wrapper>
      {!isNativeWebView && (
        <BackButton onClick={history.goBack}>
          <BackIcon>
            <span className="ion-ios-arrow-back" />
          </BackIcon>
          Back
        </BackButton>
      )}
      <UserOnly goLogin>
        <Page
          title="Fantasy Insider"
          onBack={() => history.push('/lobby')}
          disableReferrerBack
          fullWidth
        >
          <Helmet title="Fantasy Insider" />
          <Logo />
          <SportTabs
            onSelect={sport => {
              if (selectedSport && selectedSport.id === sport.id) return;
              handleSelectSport(sport);
            }}
            selectedSport={selectedSport}
            sportsList={enabledSportList}
            // old sportsList prop
            // sportsList={sportsList.filter(
            //   s => enabledSports.includes(s.slug) || enabledSportShortNames.includes(s.slug)
            // )}
          />
          <Row>
            <StyledFlexiSelect
              loading={false}
              onChange={handleSelectSlate}
              options={slateOptions}
              placeholder="Select Slate"
              value={get('id', selectedSlate)}
            />
            {!isNativeWebView && (
              <Button autoBorderRadius={false} kind="secondary" link="/faqs/cruncher">
                <div className="buttonInner">
                  <span>How To Use&nbsp;&nbsp;</span>
                  <FontAwesomeIcon icon={faQuestionCircle} />
                </div>
              </Button>
            )}
          </Row>
          {selectedSlate && (
            <Tabs
              key={`tabs_${selectedSlate.id}`}
              activeTab={activeTab}
              bonuses={bonuses}
              labels={labels}
              scheme={scheme}
              sportHasTeam={selectedSlate.sportHasTeam}
              useAverage={selectedSlate.useAverage}
              onSelect={setActiveTab}
              activeSettingsTab={activeSettingsTab}
              onSelectSettingsTab={setActiveSettingsTab}
              onClose={() => setActiveTab(undefined)}
              slateId={selectedSlate.id}
              tabs={selectedSlate.sportHasTeam ? tabs : tabsWithoutTeam}
              isCaptainContest={isCaptainContest}
            />
          )}
          <div>
            {preparing && (
              <LoaderWrapper>
                <FriendsLoader showOverlay>Preparing Cruncher...</FriendsLoader>
              </LoaderWrapper>
            )}
            {!preparing && !isEmpty(entities) && !isEmpty(insiderPlayers) && (
              <LineUpBuilder
                entities={entities}
                isCrunching={isCrunching}
                sportHasTeam={selectedSlate && selectedSlate.sportHasTeam}
                useAverage={selectedSlate && selectedSlate.useAverage}
                onCrunchTeams={handleCrunchTeams}
                keepCurrentLineUps={keepCurrentLineUps}
                onSelectSettingsTab={handleOpenSettingsTab}
                onToggleKeepCurrentLineUps={() => setKeepCurrentLineUps(!keepCurrentLineUps)}
                isCaptainContest={isCaptainContest}
              />
            )}
            {error && <FormValidationMessage kind="danger" message={error} />}
            {teams && (
              <LineUpGrid
                lineUps={Object.values(teams)}
                savedLineUps={savedLineUps}
                entities={entities}
                requiredSelections={requiredSelections}
                slateId={selectedSlate.id}
                lineUpErrors={lineUpErrors}
                allLineUpsSaved={allLineUpsSaved}
                maxExposurePerPlayer={maxExposurePerPlayer}
                keepCurrentLineUps={keepCurrentLineUps}
                onToggleKeepCurrentLineUps={() => setKeepCurrentLineUps(!keepCurrentLineUps)}
                onCrunchTeams={handleCrunchTeams}
                onResetEntered={handleResetEntered}
                onMarkEntered={handleMarkEntered}
                onClearLineUps={handleClearLineUps}
                onSaveLineUps={handleSaveLineUps}
                onRecalculate={handleRecalculate}
                onDeleteLineUp={handleDeleteLineUp}
                onRemovePlayer={handleRemovePlayer}
                sportHasTeam={selectedSlate.sportHasTeam}
              />
            )}
            {isCrunching && (
              <LoaderWrapper>
                <FriendsLoader showOverlay>Crunching...</FriendsLoader>
              </LoaderWrapper>
            )}
          </div>
          {!!message && (
            <Modal isOpen onCancel={handleCloseMessage} shouldCloseOnOverlayClick size="small">
              <h3>{message}</h3>
              <Button block kind="primary" onClick={handleCloseMessage}>
                Close
              </Button>
            </Modal>
          )}
          {!!showTermsModal && enableTermsModal && (
            <TermsModal
              siteName={siteName}
              onCancel={handleRejectTerms}
              onConfirm={handleConfirmTerms}
            />
          )}
        </Page>
      </UserOnly>
    </Wrapper>
  );
};

FantasyInsider.defaultProps = defaultProps;

const SPORTS_WITHOUT_TEAM = [GOLF];

const decodeSlate = data => {
  const sportSlug = slugify(get('competition_type.sport.name', data), { lower: true });
  const sportShortName = normaliseSportShortName(get('competition_type.short_name', data));

  return {
    id: data.id,
    isGolf: isChallengeGolf(get('competition_type.sport.onesport_id', data)),
    isCaptainContest: data.is_captain_contest,
    name: data.name,
    sportId: data.sport_id,
    sportSlug,
    sportShortName,
    startTime: data.start_time,
    sportHasTeam: SPORTS_WITHOUT_TEAM.includes(toString(get('sport_id', data))) === false,
    useAverage: shouldUseAverage(sportSlug, sportShortName),
    oneSportCompetitionId: get('competition_type.onesport_id', data),
    competitionType: {
      id: get('competition_type.id', data),
      name: get('competition_type.short_name', data),
      sportIcon: get('competition_type.sport.icon', data)
    },
    challengeRuleType: get('challenge_rule.type', data),
    selectionRules: {
      salaryCap: get('rule_set.selection_rules.salary_cap', data),
      playersInTeam: get('rule_set.selection_rules.players_in_team', data)
    },
    positions: keyBy(
      'shortLabel',
      [
        ...(get('rule_set.selection_rules.position_rules', data) || []),
        get('rule_set.selection_rules.team_selections', data) || {}
      ].map(({ short_label: shortLabel, min, max }) => ({ shortLabel, min, max }))
    )
  };
};

const SPORT_CRICKET = 3;
const COMPETITION_TYPE_BBL = 53;
const COMPETITION_TYPE_ODI = 16;
const COMPETITION_TYPE_T20 = 17;
const COMPETITION_TYPE_IT20L = 54;

const apiWrapper = compose(
  withAPICollection({
    fetcher: getSlates,
    normalizeData: flow(
      filter(slate => {
        const pass =
          get('in_lobby_contests', slate) > 0 &&
          (toString(get('competition_type.sport.onesport_id', slate)) !== GOLF ||
            get('games[0].round', slate) === 0) &&
          (get('competition_type.sport.id', slate) !== SPORT_CRICKET ||
            get('competition_type.onesport_id', slate) === COMPETITION_TYPE_BBL ||
            get('competition_type.onesport_id', slate) === COMPETITION_TYPE_ODI ||
            get('competition_type.onesport_id', slate) === COMPETITION_TYPE_T20 ||
            get('competition_type.onesport_id', slate) === COMPETITION_TYPE_IT20L);

        return pass;
      }),
      sortBy('start_time'),
      map(decodeSlate)
    )
  }),
  withProps(props => ({
    slates: props.items.filter(s =>
      (props.meta.cruncher_enabled_competition_types || []).includes(get('competitionType.id', s))
    ),
    slatesInitialised: props.initialised
  }))
);

const mapStateToProps = state => {
  const entities = entitiesSelector(state, { isCruncher: true });

  return {
    bonuses: get('challenge.bonuses', state),
    labels: get('challenge.labels', state),
    competitionName: get('challenge.competitionName', state),
    scheme: state.settings.darkMode ? 'dark' : 'light',
    isLoading: state.challenge.isLoading,
    isSingleGameChallenge: (state.challenge.games || []).length === 1,
    entities,
    sportsInitialised: state.sportCategories.hasInitialised,
    challengeTeams: state.challenge.teams,
    requiredSelections: state.challenge.playerQuota + state.challenge.teamQuota,
    siteName: state.settings.siteName,
    selectedTeams: state.playerList.selectedTeams,
    sportsList: values(state.sportCategories.items),
    defaultMaxPlayersPerTeam:
      get('challenge.playersPerSideCap', state) || get('challenge.playerQuota', state),
    defaultSalaryCap: get('challenge.salaryCap', state),
    isAFL: isChallengeAFL(get('challenge.competitionId', state)),
    insiderPlayers: get('fantasyInsider.players', state),
    insiderInitialised: get('fantasyInsider.isInitialised', state),
    insiderSettings: get('fantasyInsider.settings', state),
    hasInitialised: state.challenge.hasInitialised,
    lineUp: lineUpSelector(state),
    isNativeWebView: state.ui.isNativeWebView
  };
};

function mapDispatchToProps(dispatch) {
  return {
    getCategories() {
      dispatch(getSportCategories());
    },
    loadChallenge(id) {
      dispatch(fetchChallenge(id, { force: true }));
    },
    onResetChallenge() {
      dispatch(resetChallenge());
    },
    onResetLineUp() {
      dispatch(resetLineUp());
    },
    onResetFantasyInsider() {
      dispatch(resetFantasyInsier());
    },
    onSetInsiderPlayers(players) {
      dispatch(setPlayers(players));
    },
    onUpdatePlayers(players) {
      dispatch(updatePlayers(players));
    },
    onTeamsChange: teamId => dispatch(toggleSelectedTeamsFilter(teamId)),
    onUpdateSettings(data) {
      dispatch(updateSettings(data));
    },
    onResetExposureSettings(settings) {
      dispatch(resetExposureSettings(settings));
    }
  };
}

const connector = connect(mapStateToProps, mapDispatchToProps);

export default compose(withRouter, apiWrapper, connector)(FantasyInsider);

const BackButton = styled.a(({ theme }) => ({
  alignItems: 'center',
  color: theme.color.basis5,
  display: 'flex',
  flexDirection: 'row',
  lineHeight: 1,
  textTransform: 'uppercase',
  transition: 'color 0.2s',
  ...hover({
    color: 'white',
    textDecoration: 'none'
  })
}));

const BackIcon = styled.span(({ theme }) => ({
  fontSize: theme.fontSize.xlarge,
  marginRight: theme.spacing.small
}));

const Wrapper = styled.div`
  button {
    outline: none;
    border: 0;
  }
`;
